import { type ReactNode, useCallback, useId, useMemo, useRef, useState } from "react";
import type { TamaguiElement } from "tamagui";
import { ChevronDown } from "@tamagui/lucide-icons";
import { khTekaLineHeight } from "../../themes/token-fontSizes";
import { useControlledField } from "../../utils/useControlledField";
import { useMultiValueField } from "../../utils/useMultiValueField";
import { useInSheet } from "../Dropdown/useInSheet";
import { FieldShell, type FieldValidation, MultiFieldFooter } from "../FieldShell";
import { Listbox, type ListboxOption, type ListboxOptionProps } from "../Listbox";
import { YStack } from "../Stacks";
import { Body1 } from "../Typography";

// One option shape, owned by Listbox — aliased here so Select can't drift from it.
export type SelectOption = ListboxOption;
type SelectBaseProps = {
  options: SelectOption[];
  placeholder?: string;
  helperText?: string;
  validation?: FieldValidation;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  /** Fires once per engagement, on whichever ends it first: trigger DOM blur
   *  (web) or menu dismiss without a pick (Escape / outside click / native sheet
   *  swipe-down — the trigger has no DOM blur on native). An outside click does
   *  both; the once-guard collapses it to a single report. Lets a form adapter
   *  mark the field touched so a blur-gated required message can reveal without
   *  waiting for submit. */
  onBlur?: () => void;
  /** Escape hatch for custom rows; Listbox still owns selection, keyboard, and
   *  ids. Spread `optionProps` onto a `ListboxOption` (use its
   *  `leading`/`trailing` slots) or your own element. */
  renderOption?: (option: SelectOption, optionProps: ListboxOptionProps) => ReactNode;
};

// Single vs multi is a discriminated union — multi shows a count in the trigger
// and the picks as removable chips below the field.
type SelectSingleProps = {
  multiple?: false;
  value?: string;
  onValueChange?: (value: string) => void;
  values?: never;
  onValuesChange?: never;
};
type SelectMultiProps = {
  multiple: true;
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  value?: never;
  onValueChange?: never;
};

// Require at least one accessible name — a visible `label`, an `aria-label`, or
// both — so the combobox is never nameless to screen readers.
export type SelectProps = SelectBaseProps & (SelectSingleProps | SelectMultiProps) & ({
  label: string;
  "aria-label"?: string;
} | {
  label?: string;
  "aria-label": string;
});

/**
 * v2 dropdown: the trigger reuses the read-only `FieldShell` frame (floating
 * label + selected value + chevron); the shared `Listbox` brain owns the option
 * menu, selection, and keyboard, presenting on the `Dropdown` surface (anchored
 * popover on web — including mobile-web — bottom sheet in the native app).
 * Single-select by default; pass `multiple` for a leading-checkbox menu (toggle +
 * keep-open) with the picks as removable chips below the field.
 */
export const Select = (props: SelectProps) => {
  const {
    options,
    label,
    placeholder,
    helperText,
    validation = "none",
    disabled = false,
    required = false,
    id,
    "aria-label": ariaLabel,
    onBlur,
    renderOption
  } = props;
  const multiple = props.multiple === true;
  // Sheet (native) vs popover (web, including iOS Safari) — drives touch-open.
  const inSheet = useInSheet();
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const labelId = label ? `${baseId}-label` : undefined;
  const helperTextId = helperText ? `${baseId}-helper` : undefined;

  // Both selection fields are plumbed (hooks must be unconditional); only the
  // one matching the current mode is read.
  const single = useControlledField<string>({
    value: props.multiple === true ? undefined : props.value,
    onChange: props.multiple === true ? undefined : props.onValueChange
  });
  const multi = useMultiValueField({
    values: props.multiple === true ? props.values : undefined,
    onValuesChange: props.multiple === true ? props.onValuesChange : undefined,
    options
  });
  const selectedValue = single.value;
  const {
    values: selectedValues,
    setValues,
    removeValue,
    selectedItems
  } = multi;
  const selectedOption = useMemo(() => options.find(option => option.value === selectedValue), [options, selectedValue]);
  const hasValue = multiple ? selectedValues.length > 0 : selectedOption !== undefined;

  // Trigger DOM focus (distinct from menu-open) so the FieldShell frame border is
  // the focus indicator — no second outline around the whole component.
  const [domFocused, setDomFocused] = useState(false);
  const triggerRef = useRef<TamaguiElement>(null);
  const focusTrigger = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }, []);

  // Exactly-once guard for `onBlur`: an outside click both blurs the trigger and
  // closes the menu, which would otherwise report twice. Re-arms on the next
  // engagement (trigger focus or menu open).
  const blurReported = useRef(false);
  const reportBlur = useCallback(() => {
    if (!blurReported.current) {
      blurReported.current = true;
      onBlur?.();
    }
  }, [onBlur]);
  const handleOpenChange = useCallback((next: boolean) => {
    // Block opening while disabled, but always allow closing — so a menu that
    // was open when `disabled` flipped on can still be dismissed.
    setOpen(next && !disabled);
    if (next) {
      blurReported.current = false;
    } else {
      // A dismiss (Escape / outside click / sheet swipe-down) is the field's
      // blur on native, where the trigger has no DOM blur to lean on.
      reportBlur();
    }
  }, [disabled, reportBlur]);

  // Single: select closes the menu (Select owns `open`). Multi: toggle and keep
  // the menu open so the user can pick several before dismissing.
  const handleValueChange = useCallback((next: string) => {
    if (disabled) {
      return;
    }
    single.setValue(next);
    setOpen(false);
  }, [disabled, single.setValue]);
  const handleValuesChange = useCallback((next: string[]) => {
    if (disabled) {
      return;
    }
    setValues(next);
  }, [disabled, setValues]);
  const labelFloated = Boolean(label) && (open || hasValue);
  const focused = (open || domFocused) && !disabled;
  // Gate the menu on `disabled` too, so one that was open when `disabled` flipped
  // on is hidden immediately (not just blocked from re-opening).
  const menuOpen = open && !disabled;
  const showPlaceholder = !hasValue && (!label || labelFloated) && Boolean(placeholder);
  const valueText = multiple ? `${selectedValues.length} selected` : selectedOption?.label ?? "";
  const displayText = hasValue ? valueText : showPlaceholder ? placeholder : "";

  // Field identity only — the combobox aria (role / expanded / controls /
  // activedescendant) and keyboard are stamped by `Listbox`.
  const trigger = <YStack ref={triggerRef} tabIndex={disabled ? -1 : 0} aria-label={ariaLabel} aria-labelledby={ariaLabel ? undefined : labelId} aria-invalid={validation === "error"} aria-disabled={disabled} aria-required={required} aria-describedby={helperTextId} onFocus={() => {
    setDomFocused(true);
    blurReported.current = false;
  }} onBlur={() => {
    setDomFocused(false);
    reportBlur();
  }} cursor={disabled ? "not-allowed" : "pointer"}
  // The FieldShell frame border is the focus indicator; suppress the browser
  // outline so it doesn't draw a second box around the field + helper text.
  outlineStyle="none">
      <FieldShell validation={validation} disabled={disabled} focused={focused} label={label} labelFloated={labelFloated} labelId={labelId} required={required}
    // Multi renders the helper text below the chips (via FieldHelperRow), so
    // it doesn't wedge between the field and its value chips. Single keeps it
    // inside FieldShell as usual.
    helperText={multiple ? undefined : helperText} helperTextId={helperTextId} right={ChevronDown}
    // Touch: a tap doesn't actuate the Popover.Trigger clone (same gap the
    // Combobox touch trigger works around), so open the menu from an explicit
    // frame press. Web keeps relying on the trigger clone's click.
    onPress={inSheet && !disabled ? () => handleOpenChange(true) : undefined} pressable={!disabled}>
        <Body1 numberOfLines={1}
      // Reserve one body1 line even when empty (label-only / placeholder
      // suppressed) so FieldShell's resting-label math centers like Input,
      // whose text control always occupies a line.
      minHeight={khTekaLineHeight.body1} color={hasValue ? "$textDefault" : "$textSubtle"}>
          {displayText}
        </Body1>
      </FieldShell>
    </YStack>;

  // The prop union guarantees one of label/aria-label; rebuild it for Listbox.
  const nameProps: {
    label: string;
  } | {
    "aria-label": string;
  } = label !== undefined ? {
    label
  } : {
    "aria-label": ariaLabel ?? ""
  };

  // Single vs multi differ by 3 selection props; spread them into one Listbox.
  const selectionProps = multiple ? {
    multiple: true as const,
    values: selectedValues,
    onValuesChange: handleValuesChange
  } : {
    value: selectedValue ?? null,
    onValueChange: handleValueChange
  };
  return <YStack data-sentry-element="YStack" data-sentry-component="Select" data-sentry-source-file="Select.tsx">
      <Listbox {...selectionProps} options={options} open={menuOpen} onOpenChange={handleOpenChange} trigger={trigger} matchTriggerWidth {...nameProps} data-sentry-element="Listbox" data-sentry-source-file="Select.tsx">
        <Listbox.Options renderOption={renderOption} data-sentry-element="Listbox.Options" data-sentry-source-file="Select.tsx" />
      </Listbox>
      {multiple ? <MultiFieldFooter items={selectedItems} onRemove={removeValue} groupLabel={label ?? ariaLabel ?? ""} disabled={disabled} validation={validation} helperText={helperText} helperTextId={helperTextId} focusAfterLastRemove={focusTrigger} /> : null}
    </YStack>;
};